import React from 'react';
import { Trash2, ToggleLeft, ToggleRight, Clock, Mail, Calendar } from 'lucide-react';
import type { ReportSchedule } from '@/types/reportScheduler';

interface ScheduleListProps {
  schedules: ReportSchedule[];
  onToggle: (id: number, isActive: boolean) => void;
  onDelete: (id: number) => void;
  onEdit: (schedule: ReportSchedule) => void;
  loading: boolean;
}

const DAYS_OF_WEEK_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const formatTime = (time: string) => {
  if (!time) return '';
  const [hours, minutes] = time.split(':');
  const h = parseInt(hours, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const displayH = h % 12 || 12;
  return `${displayH}:${minutes} ${ampm}`;
};

const formatDateShort = (dateStr: string) => {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const getFrequencyLabel = (schedule: ReportSchedule) => {
  const time = formatTime(schedule.time_of_day);

  switch (schedule.frequency) {
    case 'once': {
      const dates = schedule.specific_dates || [];
      if (dates.length === 1) return `Once on ${formatDateShort(dates[0])} at ${time}`;
      if (dates.length <= 3) return `On ${dates.map(formatDateShort).join(', ')} at ${time}`;
      return `On ${dates.length} specific dates at ${time}`;
    }

    case 'daily':
      return `Daily at ${time}`;

    case 'weekdays':
      return `Weekdays (Mon-Fri) at ${time}`;

    case 'weekly': {
      const days = schedule.days_of_week || (schedule.day_of_week != null ? [schedule.day_of_week] : []);
      if (days.length === 1) return `Every ${DAYS_OF_WEEK_SHORT[days[0]]} at ${time}`;
      if (days.length <= 3) return `Every ${days.map(d => DAYS_OF_WEEK_SHORT[d]).join(', ')} at ${time}`;
      return `${days.length} days/week at ${time}`;
    }

    case 'biweekly': {
      const days = schedule.days_of_week || (schedule.day_of_week != null ? [schedule.day_of_week] : []);
      if (days.length === 1) return `Every other ${DAYS_OF_WEEK_SHORT[days[0]]} at ${time}`;
      return `Biweekly on ${days.map(d => DAYS_OF_WEEK_SHORT[d]).join(', ')} at ${time}`;
    }

    case 'monthly': {
      const days = schedule.days_of_month || (schedule.day_of_month != null ? [schedule.day_of_month] : []);
      if (days.length === 1) return `Monthly on day ${days[0]} at ${time}`;
      if (days.length <= 3) return `Monthly on days ${days.join(', ')} at ${time}`;
      return `Monthly on ${days.length} days at ${time}`;
    }

    case 'custom': {
      const dates = schedule.specific_dates || [];
      if (dates.length <= 2) return `Custom: ${dates.map(formatDateShort).join(', ')} at ${time}`;
      return `Custom: ${dates.length} dates at ${time}`;
    }

    default:
      return schedule.frequency;
  }
};

const ScheduleList: React.FC<ScheduleListProps> = ({ schedules, onToggle, onDelete, onEdit, loading }) => {
  if (loading) {
    return <div className="schedule-list-empty">Loading schedules...</div>;
  }

  if (schedules.length === 0) {
    return (
      <div className="schedule-list-empty">
        <Calendar size={24} />
        <p>No scheduled reports yet. Create one above.</p>
      </div>
    );
  }

  return (
    <div className="schedule-list">
      {schedules.map((schedule) => (
        <div
          key={schedule.id}
          className={`schedule-list-item ${!schedule.is_active ? 'schedule-inactive' : ''}`}
        >
          <div className="schedule-list-item-info" onClick={() => onEdit(schedule)}>
            {schedule.schedule_name && (
              <div className="schedule-list-item-name">{schedule.schedule_name}</div>
            )}
            <div className="schedule-list-item-frequency">
              <Clock size={14} />
              <span>{getFrequencyLabel(schedule)}</span>
            </div>
            <div className="schedule-list-item-recipients">
              <Mail size={14} />
              <span>{schedule.recipients.join(', ')}</span>
            </div>
            {schedule.repeat_end_date && (
              <div className="schedule-list-item-last-sent">
                Ends: {new Date(schedule.repeat_end_date + 'T00:00:00').toLocaleDateString()}
              </div>
            )}
            {schedule.last_sent_at && (
              <div className="schedule-list-item-last-sent">
                Last sent: {new Date(schedule.last_sent_at).toLocaleString()}
              </div>
            )}
          </div>
          <div className="schedule-list-item-actions">
            <button
              className="schedule-toggle-btn"
              onClick={() => onToggle(schedule.id, !schedule.is_active)}
              title={schedule.is_active ? 'Deactivate' : 'Activate'}
            >
              {schedule.is_active ? (
                <ToggleRight size={22} className="toggle-active" />
              ) : (
                <ToggleLeft size={22} className="toggle-inactive" />
              )}
            </button>
            <button
              className="schedule-delete-btn"
              onClick={() => onDelete(schedule.id)}
              title="Delete schedule"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ScheduleList;
